import type { CheckOptions, GroupSpec, LanguageDiagnostic, LanguageSymbol } from '@vitesse/statechart-host';
import { stripForEngine, typeCheckForEngine } from '../../sandbox/statechart/typescript';

/*
 * THE DOCUMENT IS CHECKED BEFORE IT IS SENT, REPLAYED OR READ.
 *
 * The model-runner refuses a document it cannot run, but it names the first fault it meets and no
 * other, so an author with three faults spent three writes finding them — and the tool breaker ends
 * the run at the third refusal in a row. It also runs two things it should never be handed: a bare <
 * in a cond, which the engine reads and the statechart view cannot draw (0121), and a name nothing
 * declares, which runs until the expression is first evaluated and fails every time after. A
 * threshold spelled `clearAT` beside a `<data id="clearAt">` boots, raises its alarm, and never
 * clears it.
 *
 * `checkModel` is the engine's language service over the document as written, with the runner's
 * reading of it: an error there is a document the runner refuses (statechart-host's check.test.ts
 * holds the two together). The tools refuse on those, and on an unknown name, which the runner only
 * warns about. Every finding is listed with its line and that line's text, so one rewrite can fix
 * them all. Warnings refuse nothing; they ride on the answer, for the author to judge.
 *
 * Three tools ask: models.write before it sends a document, models.simulate before it replays one,
 * and models.read about the one that is saved, with its outline. Each says it in its own words; the
 * list is the same in all three, so a fault reads the same wherever the author meets it.
 *
 * Loaded on first use, as the Simulate section loads the adapter: the shell has no need of the
 * language service until a model is written, replayed or read. If it cannot be loaded, or fails on a
 * document, the tool goes on without it — the model-runner is still the gate it always was.
 */

/** Each finding costs the author two lines of context: enough to fix a document in one rewrite, not enough to drown it. */
export const MAX_FINDINGS = 20;
/** One line of SCXML can be a whole minified document. */
const MAX_SOURCE_CHARS = 160;

export interface DocumentCheck {
  /** What refuses a write or a replay: the runner's errors, and a name nothing declares. */
  refusing: LanguageDiagnostic[];
  warnings: LanguageDiagnostic[];
}

/** LSP's `DiagnosticSeverity.Error`: the protocol's own number, so this needs no loaded service to compare against. */
const ERROR = 1;

/**
 * What refuses a write or a replay, and what the model page underlines in red: the runner's errors,
 * and a name nothing declares, which the runner boots and which fails each time it is evaluated.
 */
export const isRefusal = (d: LanguageDiagnostic): boolean => d.severity === ERROR || d.code === 'unknown-identifier';

function split(found: LanguageDiagnostic[]): DocumentCheck {
  return { refusing: found.filter(isRefusal), warnings: found.filter((d) => !isRefusal(d)) };
}

/**
 * The check, or null when the language service could not be loaded or could
 * not read the document. A `typescript` document is checked as the engine
 * reads it, with its types blanked, so a type is not a syntax error; the
 * strip's own findings (an `enum`, a child document in TypeScript) are listed
 * with the rest, at their lines. Its types are checked too, against the
 * ports' declared types in `options.types`, and the proposal's rules apply
 * (Part 3 of docs/hierarchical-models-step-3-plan.md). A type check that
 * cannot run is left out: the relay checks the types again at the save.
 */
export async function checkDocument(scxml: string, options: CheckOptions): Promise<DocumentCheck | null> {
  try {
    const [{ checkModel }, strip] = await Promise.all([import('@vitesse/statechart-host'), stripForEngine(scxml)]);
    const typeCheck = await typeCheckForEngine(scxml, strip, options);
    return split(checkModel(scxml, { ...options, strip, ...(typeCheck ? { typeCheck } : {}) }));
  } catch {
    return null;
  }
}

export const problems = (n: number): string => (n === 1 ? '1 problem' : `${n} problems`);

/*
 * THE WIRING OF A SAVE, CHECKED BEFORE IT IS SENT (Part 4 of docs/hierarchical-models-step-3-plan.md).
 *
 * The relay checks every save that can change the wiring or the types, and refuses with the
 * finding's words. The page checks first, with what it knows, so a refusal costs no round trip:
 * a connection to a symbol whose type does not fit the input (the bindings editor knows the type
 * of the symbol it offers), and an output that another model of the gateway still reads (the
 * models list carries every model's wiring). A path whose type the page does not know is left to
 * the relay, which reads the symbol's descriptor. The bindings editor has no template texts, so a
 * group's children are left to the relay too.
 */

/** The root's save as the wiring check reads it: its ports, and its own wiring after the save. */
export interface WiringSave {
  modelId: string;
  scxml: string;
  inputs: readonly string[];
  outputs: readonly string[];
  types: Readonly<Record<string, string>>;
  wiring: NonNullable<GroupSpec['root']['wiring']>;
}

/** Another model of the gateway, as the page lists it: the paths its root's wiring reads, and its children's. */
export interface WiringReader {
  id: string;
  wiring?: { inputs: ReadonlyArray<{ name: string; path: string }> };
  children?: ReadonlyArray<{ name: string; template?: string; count?: number; inputs: ReadonlyArray<{ name: string; path: string }> }>;
}

/** The host's `pathsFor`: a path for each machine of an array child when the path holds `{i}` or `{n}`, else the path itself. */
export type PathsFor = (child: { name: string; template: string; count?: number }, path: string) => string[];

/**
 * Every path the gateway's other models read: their root's wiring and their children's inputs.
 * With the host's `pathsFor`, a path of an array child with an index gives a reader for each
 * machine, named as the host names it, `sensors[3].raw`, as model-runner's `readersOf` does. Without
 * it, the path stays as written, and no output is named `lamp{n}`, so such a reader is lost to the check.
 *
 * A model with more readers than `limit` (the host's MAX_GROUP_SIZE) gives none: it is too large to
 * run, as model-runner's `readersOf` finds. Its expansion stops there, so not all its paths are built.
 */
export function readersOf(
  others: readonly WiringReader[],
  modelId: string,
  pathsFor?: PathsFor,
  limit = Infinity,
): Array<{ model: string; port: string; path: string }> {
  const readersOfModel = (m: WiringReader): Array<{ model: string; port: string; path: string }> => {
    const readers = (m.wiring?.inputs ?? []).filter((p) => p.path).map((p) => ({ model: m.id, port: p.name, path: p.path }));
    for (const c of m.children ?? []) {
      const child = { name: c.name, template: c.template ?? '', ...(c.count !== undefined ? { count: c.count } : {}) };
      for (const p of c.inputs) {
        if (!p.path) continue;
        const paths = pathsFor ? pathsFor(child, p.path) : [p.path];
        if (paths.length === 1 && paths[0] === p.path) readers.push({ model: m.id, port: `${c.name}.${p.name}`, path: p.path });
        else paths.forEach((path, i) => readers.push({ model: m.id, port: `${c.name}[${i}].${p.name}`, path }));
        if (readers.length > limit) return [];
      }
    }
    return readers;
  };
  return others.filter((m) => m.id !== modelId).flatMap(readersOfModel);
}

/**
 * Does any transition in the document handle this command?
 *
 * READ AS A LIST, NOT MATCHED INSIDE A STRING. This was a regex that looked for the name inside the
 * `event="…"` attribute bounded by `(?:^|\s)` and `(?:\s|$|\.)` — and `^`/`$` anchor to the whole
 * SCXML document, never to the attribute. So the boundary could only ever be satisfied by a SPACE,
 * which means `event="reset"` — the ordinary spelling — never matched, while `event="go reset stop"`
 * did. Three of the four legal forms were refused, so a declared command was effectively impossible
 * to write, and an author that had correctly written `<transition event="reset">` was told no such
 * transition existed. It rewrote the same document and was refused identically.
 *
 * An `event` attribute is a space-separated list of descriptors, so splitting it is both simpler and
 * correct. Descriptors match by prefix in SCXML — a transition on `reset` fires for `reset.done` —
 * and a handler written as `reset.done` is still a handler written for the reset command, so both
 * directions count. `*` handles everything.
 */
export const handlesCommand = (scxml: string, name: string): boolean => {
  for (const m of scxml.matchAll(/<transition\s[^>]*\bevent="([^"]*)"/g)) {
    for (const token of (m[1] ?? '').trim().split(/\s+/)) {
      if (!token) continue;
      if (token === '*' || token === name) return true;
      if (token.startsWith(`${name}.`) || name.startsWith(`${token}.`)) return true;
    }
  }
  return false;
};

/** The refusals and the warnings of the wiring check, in the relay's words; null when the check could not be loaded. */
export async function checkWiringSave(
  save: WiringSave,
  others: readonly WiringReader[],
  typeOf?: (path: string) => string | undefined,
): Promise<{ refusing: string[]; warnings: string[] } | null> {
  try {
    const [{ checkWiring, MAX_GROUP_SIZE, pathsFor }, strip] = await Promise.all([
      import('@vitesse/statechart-host'),
      stripForEngine(save.scxml),
    ]);
    const readers = readersOf(others, save.modelId, pathsFor, MAX_GROUP_SIZE);
    const found = checkWiring(
      {
        root: { scxml: strip.text, inputs: save.inputs, outputs: save.outputs, types: save.types, wiring: save.wiring },
        templates: {},
        children: [],
      },
      { modelId: save.modelId, readers, ...(typeOf ? { typeOf } : {}) },
    );
    return {
      refusing: found.filter((f) => f.severity === 'error').map((f) => f.message),
      // Only what the page knows: a path whose type it does not know is the relay's to judge.
      warnings: found.filter((f) => f.severity === 'warning' && f.code !== 'type-unknown').map((f) => f.message),
    };
  } catch {
    return null;
  }
}

/** Each finding with its line and column, counted from one, and the line it is on; one with no position is about the whole model. */
export function listFindings(scxml: string, found: LanguageDiagnostic[]): string {
  const lines = scxml.split(/\r\n|\n/);
  const listed = found.slice(0, MAX_FINDINGS).map((d) => {
    const { start, end } = d.range;
    // The service places a finding with no position at the very start, with no width.
    if (start.line === 0 && start.character === 0 && end.line === 0 && end.character === 0) return `(the model)  ${d.code}  ${d.message}`;
    const source = (lines[start.line] ?? '').trim();
    const shown = source.length > MAX_SOURCE_CHARS ? `${source.slice(0, MAX_SOURCE_CHARS)}…` : source;
    return `line ${start.line + 1}, col ${start.character + 1}  ${d.code}  ${d.message}\n    ${shown}`;
  });
  if (found.length > MAX_FINDINGS) listed.push(`… and ${found.length - MAX_FINDINGS} more.`);
  return listed.join('\n');
}

const UNKNOWN_NAME =
  '\nAn unknown name is a misspelling or a missing <data id="…">: the model would boot, then fail every time that expression is evaluated.';

/** A refusal: the tool's own lead sentence, every problem, and what an unknown name is when there is one. */
export const refusalMessage = (lead: string, scxml: string, refusing: LanguageDiagnostic[]): string =>
  `${lead}\n${listFindings(scxml, refusing)}${refusing.some((d) => d.code === 'unknown-identifier') ? UNKNOWN_NAME : ''}`;

/*
 * THE OUTLINE — THE WHOLE MODEL'S SHAPE, WHEN THE WHOLE MODEL IS TOO LONG TO GIVE.
 *
 * models.read gives the first MAX_SCXML_CHARS of the source and no more. For a long model that is a
 * fraction of it (the 400-state test model is 207,280 characters; the reader sees about its first 45
 * states), and an author that cannot see a state cannot explain it, find it, or avoid reusing its id.
 * The outline is every state with its kind and line, and every datamodel variable with its kind as
 * the runner's slot map makes it — input, output or internal, which the source alone does not say.
 *
 * WITH A LIMIT, because every tool reply stays in the conversation and is sent again on each later
 * step. That model's full outline is 444 lines; beside the cut source, about 9,000 tokens a step.
 * Past OUTLINE_MAX_LINES it is the datamodel and the top-level states, each with the number of
 * states inside it — about 45 lines for that model — and the answer says it was shortened.
 */
export const OUTLINE_MAX_LINES = 150;
const SHORT_MAX_VARIABLES = 50;
const SHORT_MAX_STATES = 100;

export interface ModelOutline {
  text: string;
  shortened: boolean;
  /** The full outline's length in lines, shortened or not. */
  lines: number;
}

const isDatamodel = (s: LanguageSymbol): boolean => s.name === 'datamodel' && !s.detail;
const entry = (s: LanguageSymbol, extra = ''): string =>
  `${s.name}  (${[s.detail, extra, `line ${s.range.start.line + 1}`].filter(Boolean).join(', ')})`;
const inside = (s: LanguageSymbol): number => (s.children ?? []).reduce((n, c) => n + 1 + inside(c), 0);

export function outlineText(symbols: LanguageSymbol[]): ModelOutline {
  const full: string[] = [];
  const walk = (list: LanguageSymbol[], depth: number): void => {
    for (const s of list) {
      full.push(`${'  '.repeat(depth)}${entry(s)}`);
      walk(s.children ?? [], depth + 1);
    }
  };
  walk(symbols, 0);
  if (full.length <= OUTLINE_MAX_LINES) return { text: full.join('\n'), shortened: false, lines: full.length };

  const short: string[] = [];
  for (const group of symbols.filter(isDatamodel)) {
    const variables = group.children ?? [];
    short.push(entry(group, `${variables.length} variables`));
    for (const v of variables.slice(0, SHORT_MAX_VARIABLES)) short.push(`  ${entry(v)}`);
    if (variables.length > SHORT_MAX_VARIABLES) short.push(`  … and ${variables.length - SHORT_MAX_VARIABLES} more variables`);
  }
  const states = symbols.filter((s) => !isDatamodel(s));
  for (const s of states.slice(0, SHORT_MAX_STATES)) {
    const n = inside(s);
    short.push(entry(s, n === 0 ? '' : `${n} ${n === 1 ? 'state' : 'states'} inside`));
  }
  if (states.length > SHORT_MAX_STATES) short.push(`… and ${states.length - SHORT_MAX_STATES} more top-level states`);
  return { text: short.join('\n'), shortened: true, lines: full.length };
}

/** The saved model's outline and findings, from one load of the language service; null when it could not be loaded or could not read the document. */
export async function describeDocument(
  scxml: string,
  options: CheckOptions,
): Promise<{ outline: ModelOutline; check: DocumentCheck } | null> {
  try {
    const [{ readModel }, strip] = await Promise.all([import('@vitesse/statechart-host'), stripForEngine(scxml)]);
    const typeCheck = await typeCheckForEngine(scxml, strip, options);
    const reading = readModel(scxml, { ...options, strip, ...(typeCheck ? { typeCheck } : {}) });
    return { outline: outlineText(reading.document.documentSymbols()), check: split(reading.diagnostics) };
  } catch {
    return null;
  }
}
